import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import {
  selectPaymentData,
  selectDataRequired,
  selectConfirmPayment,
  cleanDataRequiredError,
  cleanConfirmPaymentError,
} from '../../features/payment/payment-slice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  styled,
  Typography,
  TypographyProps,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from 'components/common/button';
import MessageError from 'components/message-error';
import { ErrorInfo } from 'utils/error-codes';
import Dialog from 'components/common/dialog';
import { useState } from 'react';
import { TermsAndConditions } from 'components/terms-and-conditions';

const TypographySummary = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '14px',
}));

const TypographyDetail = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
}));

interface PaymentInfoProps {
  collapsible?: boolean;
}

const PaymentInfo = ({ collapsible }: PaymentInfoProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const paymentData = useAppSelector(selectPaymentData);
  const dataRequired = useAppSelector(selectDataRequired);
  const confirmPayment = useAppSelector(selectConfirmPayment);
  const dispatch = useAppDispatch();

  const [expanded, setExpanded] = useState(false);

  const paymentInfo = paymentData?.paymentInfo;
  const { instalmentSelected, inputs } = dataRequired;

  const ShowError = ({
    error,
    errorInfo,
    cleanError,
  }: {
    error: boolean;
    errorInfo: ErrorInfo | null;
    cleanError: () => void;
  }) => {
    if (isDesktop) {
      return <div className={styles.payment_info_error}>{error && <MessageError errorInfo={errorInfo} />}</div>;
    }
    return (
      <Dialog dividers={false} footer={<Button onClick={cleanError}>{t('common.ok')}</Button>} handleClose={cleanError}>
        <div className={styles.payment_info_error}>{error && <MessageError errorInfo={errorInfo} />}</div>
      </Dialog>
    );
  };

  const Detail = () => {
    return (
      <>
        {!instalmentSelected?.installmentsByBank &&
          instalmentSelected?.installments &&
          instalmentSelected?.installments !== 1 &&
          instalmentSelected?.installmentAmount && (
            <TypographyDetail>
              {t('common.in')}&nbsp;{instalmentSelected?.installments}&nbsp;{t('common.of')}&nbsp;
              {paymentInfo?.currency}&nbsp;{instalmentSelected?.installmentAmount}
            </TypographyDetail>
          )}
        {instalmentSelected?.installmentsByBank && <TypographyDetail>{t('paymentInfo.plusInterest')}</TypographyDetail>}
      </>
    );
  };

  return (
    <div className={styles.payment_info}>
      {collapsible ? (
        <Accordion
          sx={{ position: 'fixed', zIndex: 2 }}
          square
          className={styles.payment_info_accordion}
          onChange={(_, expanded: boolean) => setExpanded(expanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <TypographySummary className={styles.payment_info_left}>{t('paymentInfo.total')}</TypographySummary>
            <TypographySummary className={styles.payment_info_right} translate="no">
              {instalmentSelected?.totalAmount && paymentInfo?.total !== instalmentSelected.totalAmount ? (
                <span>
                  {paymentInfo?.currency} {instalmentSelected.totalAmount}
                </span>
              ) : (
                <span>
                  {paymentInfo?.currency} {paymentInfo?.amount}
                </span>
              )}
              {expanded && <Detail />}
            </TypographySummary>
          </AccordionSummary>
          <AccordionDetails className={styles.payment_info_details}>
            <Typography className={styles.payment_info_left}>{t('paymentInfo.subtotal')}</Typography>
            <Typography className={styles.payment_info_right}>
              {paymentInfo?.currency} {paymentInfo?.total}
            </Typography>
          </AccordionDetails>
          {paymentData?.description && (
            <AccordionDetails className={styles.payment_info_details_full}>
              <Typography className={styles.payment_info_full}>{paymentData.description}</Typography>
            </AccordionDetails>
          )}
        </Accordion>
      ) : (
        <>
          {paymentData?.description && <div className={styles.payment_info_description}>{paymentData.description}</div>}
          <div className={styles.payment_info_expanded}>
            <div className={styles.payment_info_details}>
              <Typography className={styles.payment_info_left}>{t('paymentInfo.subtotal')}</Typography>
              <Typography className={styles.payment_info_right}>
                {paymentInfo?.currency} {paymentInfo?.total}
              </Typography>
            </div>
            <Divider />
            <div className={styles.payment_info_details}>
              <TypographySummary className={styles.payment_info_left}>{t('paymentInfo.total')}</TypographySummary>
              <TypographySummary className={styles.payment_info_right} translate="no">
                {instalmentSelected?.totalAmount && paymentInfo?.total !== instalmentSelected.totalAmount ? (
                  <span>
                    <span>
                      {paymentInfo?.currency} {instalmentSelected.totalAmount}
                    </span>
                    <Detail />
                  </span>
                ) : (
                  <span>
                    <span>
                      {paymentInfo?.currency} {paymentInfo?.total}
                    </span>
                    {instalmentSelected?.installmentsByBank && (
                      <TypographyDetail>{t('paymentInfo.plusInterest')}</TypographyDetail>
                    )}
                  </span>
                )}
              </TypographySummary>
            </div>
          </div>
        </>
      )}
      {confirmPayment.error && (
        <ShowError
          error={confirmPayment.error}
          errorInfo={confirmPayment.errorInfo}
          cleanError={() => dispatch(cleanConfirmPaymentError())}
        />
      )}
      {dataRequired.error && (
        <ShowError
          error={dataRequired.error}
          errorInfo={dataRequired.errorInfo}
          cleanError={() => dispatch(cleanDataRequiredError())}
        />
      )}
      <div className={styles.payment_info_confirm}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          loading={confirmPayment.loading}
          square={!isDesktop}
          disabled={!inputs}
          onClick={() => document?.getElementById('submit-data-required')?.click()}
        >
          {t('confirmPayment')}
        </Button>
      </div>
      {isDesktop && <TermsAndConditions />}
    </div>
  );
};

export default PaymentInfo;
